import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppStore } from '../store/useAppStore';
import { qk } from '../services/queryKeys';
import {
  fetchPayrollRuns,
  fetchPayrollTemplates,
  fetchPayslips,
  fetchMyPayslips,
  startPayrollRun,
  finalizePayrollRunApi,
  markPayrollRunPaid,
  fetchBankFileCsv,
  fetchEmployees,
  fetchSalaryStructures,
  createSalaryStructureApi,
  payslipPdfUrl,
} from '../services/api';
import type { SalaryComponent, SalaryStructure } from '../services/api';
import type { PayrollRun, Payslip, Employee } from '../types';
import { formatMinor } from '../lib/money';
import Chip from '../components/Chip';
import WpsPanel from '../components/WpsPanel';
import VariableLinesTable from '../components/VariableLinesTable';
import Button from '../components/Button';
import Icon from '../components/Icon';
import Modal from '../components/Modal';
import Alert from '../components/Alert';

// ── helpers ──────────────────────────────────────────────────────────────────

/**
 * Money arrives as minor-unit strings (CR-1). The single division by 100 lives
 * in lib/money; nothing here does arithmetic on an amount.
 */
const fmt = (minor: string | number | null | undefined) =>
  formatMinor(minor, 'INR', { maximumFractionDigits: 0 });

function downloadCsv(csv: string, filename: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// Stage config: maps run status to the active stage index (0-based)
const STAGES = ['Draft', 'Inputs', 'Review', 'Finalize', 'Disburse'];

function stageIndex(status: PayrollRun['status']): number {
  if (status === 'paid') return 4;
  if (status === 'finalized') return 3;
  return 1; // draft = inputs loaded
}

// ── Stage indicator ───────────────────────────────────────────────────────────

function StageBar({ status }: { status: PayrollRun['status'] }) {
  const { theme } = useAppStore();
  const light = theme === 'light';
  const active = stageIndex(status);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 4 }}>
      {STAGES.map((s, i) => {
        const done = i < active;
        const current = i === active;
        return (
          <React.Fragment key={s}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 700,
                background: done
                  ? 'var(--success)'
                  : current
                    ? 'var(--accent)'
                    : light ? 'rgba(0,0,0,0.07)' : 'rgba(255,255,255,0.10)',
                color: done || current ? '#fff' : 'var(--ink-3)',
                border: current ? '2px solid var(--accent)' : '2px solid transparent',
              }}>
                {done ? '✓' : i + 1}
              </div>
              <span style={{
                fontSize: 10, fontWeight: current ? 700 : 500,
                color: current ? 'var(--accent)' : done ? 'var(--success)' : 'var(--ink-3)',
                whiteSpace: 'nowrap',
              }}>
                {s}
              </span>
            </div>
            {i < STAGES.length - 1 && (
              <div style={{
                flex: 1, height: 2, margin: '0 4px', marginBottom: 18,
                background: i < active
                  ? 'var(--success)'
                  : light ? 'rgba(0,0,0,0.10)' : 'rgba(255,255,255,0.12)',
              }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ── Expanded payslip rows ─────────────────────────────────────────────────────

function RunPayslips({ runId }: { runId: string }) {
  const { theme } = useAppStore();
  const light = theme === 'light';
  const { data: payslips = [], isLoading } = useQuery({
    queryKey: qk.payslips(runId),
    queryFn: () => fetchPayslips(runId),
  });

  if (isLoading) {
    return (
      <tr>
        <td colSpan={6} style={{ padding: '12px 20px', color: 'var(--ink-3)', fontSize: 'var(--fs-sm)' }}>
          Loading payslips…
        </td>
      </tr>
    );
  }

  if (!payslips.length) {
    return (
      <tr>
        <td colSpan={6} style={{ padding: '12px 20px', color: 'var(--ink-3)', fontSize: 'var(--fs-sm)' }}>
          No payslips in this run.
        </td>
      </tr>
    );
  }

  return (
    <>
      {/*
        * CH-U4 — per-run WPS panel. Renders itself away for non-WPS orgs, so an
        * India run looks exactly as it did before.
        */}
      <tr>
        <td colSpan={6} style={{ padding: '12px 20px 0 44px' }}>
          <WpsPanel runId={runId} />
        </td>
      </tr>
      {/*
        * CH-U2 — variable lines for the run: kind, component, amount, source
        * (module + ref), period, arrears badge, reversal pairs linked. No edit
        * action: corrections happen at the source vertical via a reversal.
        */}
      <tr>
        <td colSpan={6} style={{ padding: '0 20px 12px 44px' }}>
          <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', margin: '8px 0 6px', fontWeight: 600 }}>
            VARIABLE LINES
          </p>
          <VariableLinesTable runId={runId} />
        </td>
      </tr>
      <tr>
        <td colSpan={6} style={{ padding: 0 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{
                background: light ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.04)',
                fontSize: 'var(--fs-xs)', color: 'var(--ink-3)',
              }}>
                <th style={{ padding: '6px 20px 6px 44px', textAlign: 'left', fontWeight: 600 }}>Employee</th>
                <th style={{ padding: '6px 12px', textAlign: 'right', fontWeight: 600 }}>Gross</th>
                <th style={{ padding: '6px 12px', textAlign: 'right', fontWeight: 600 }}>Deductions</th>
                <th style={{ padding: '6px 20px 6px 12px', textAlign: 'right', fontWeight: 600 }}>Net Pay</th>
              </tr>
            </thead>
            <tbody>
              {payslips.map((slip: Payslip) => {
                const name = slip.employee
                  ? `${slip.employee.firstName} ${slip.employee.lastName ?? ''}`.trim()
                  : slip.employeeId;
                return (
                  <tr key={slip.id} style={{
                    borderTop: light ? '1px solid rgba(0,0,0,0.05)' : '1px solid rgba(255,255,255,0.05)',
                    fontSize: 'var(--fs-sm)',
                  }}>
                    <td style={{ padding: '8px 20px 8px 44px', color: 'var(--ink)' }}>{name}</td>
                    <td className="mono" style={{ padding: '8px 12px', textAlign: 'right', color: 'var(--ink-2)' }}>
                      {fmt(slip.grossPayMinor)}
                    </td>
                    <td className="mono" style={{ padding: '8px 12px', textAlign: 'right', color: 'var(--danger)' }}>
                      -{fmt(slip.totalDeductionsMinor)}
                    </td>
                    <td className="mono" style={{ padding: '8px 20px 8px 12px', textAlign: 'right', fontWeight: 600 }}>
                      {fmt(slip.netPayMinor)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </td>
      </tr>
    </>
  );
}

// ── Confirm finalize dialog ───────────────────────────────────────────────────

function FinalizeConfirm({ runLabel, onConfirm, onCancel }: {
  runLabel: string; onConfirm: () => void; onCancel: () => void;
}) {
  const { theme } = useAppStore();
  const light = theme === 'light';

  React.useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onCancel]);

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1100,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
        background: light ? 'rgba(29,30,39,0.3)' : 'rgba(0,0,0,0.55)',
      }}
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 400, maxWidth: 'calc(100vw - 48px)',
          backdropFilter: 'blur(28px) saturate(1.6)', WebkitBackdropFilter: 'blur(28px) saturate(1.6)',
          background: light ? 'rgba(255,255,255,0.78)' : 'rgba(38,39,56,0.80)',
          border: light ? '1.5px solid rgba(255,255,255,0.7)' : '1.5px solid rgba(255,255,255,0.10)',
          boxShadow: light
            ? '0 32px 80px rgba(29,30,39,0.18), inset 0 1px 0 rgba(255,255,255,0.9)'
            : '0 32px 80px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.07)',
          borderRadius: 20, padding: '32px 28px 24px', position: 'relative',
        }}
      >
        <div style={{
          width: 52, height: 52, borderRadius: 14, marginBottom: 20,
          background: light ? 'rgba(194,69,46,0.10)' : 'rgba(244,105,79,0.15)',
          border: light ? '1px solid rgba(194,69,46,0.18)' : '1px solid rgba(244,105,79,0.22)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon name="warn" size={24} color="var(--danger)" />
        </div>
        <h2 style={{ fontSize: 'var(--fs-xl)', fontWeight: 700, color: 'var(--ink)', marginBottom: 8 }}>
          Finalize payroll run?
        </h2>
        <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-2)', marginBottom: 6, lineHeight: 1.6 }}>
          You are about to finalize <strong style={{ color: 'var(--ink)' }}>{runLabel}</strong>.
        </p>
        <p style={{
          fontSize: 'var(--fs-sm)', color: 'var(--danger)', marginBottom: 28,
          background: light ? 'rgba(194,69,46,0.06)' : 'rgba(244,105,79,0.10)',
          border: light ? '1px solid rgba(194,69,46,0.14)' : '1px solid rgba(244,105,79,0.18)',
          borderRadius: 8, padding: '8px 12px',
        }}>
          This action is irreversible — payslips will be locked.
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button variant="danger" onClick={onConfirm}>Finalize run</Button>
        </div>
      </div>
    </div>
  );
}

// ── Month picker ─────────────────────────────────────────────────────────────

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function MonthPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { theme } = useAppStore();
  const light = theme === 'light';
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [yearOpen, setYearOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });

  const selYear  = value ? parseInt(value.split('-')[0]) : new Date().getFullYear();
  const selMonth = value ? parseInt(value.split('-')[1]) - 1 : new Date().getMonth();
  const [year, setYear] = useState(selYear);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear + 2 - i);

  const openPicker = () => {
    if (triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 6, left: r.left, width: r.width });
    }
    setOpen(o => !o);
    setYearOpen(false);
  };

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        triggerRef.current && !triggerRef.current.contains(t) &&
        dropdownRef.current && !dropdownRef.current.contains(t)
      ) {
        setOpen(false);
        setYearOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const pick = (mIdx: number) => {
    onChange(`${year}-${String(mIdx + 1).padStart(2, '0')}`);
    setOpen(false);
    setYearOpen(false);
  };

  const displayLabel = value ? `${MONTH_NAMES[selMonth]} ${selYear}` : 'Select period';

  const navBtn = (onClick: () => void, label: string) => (
    <button type="button" onClick={onClick} style={{
      width: 28, height: 28, borderRadius: 7, background: 'none',
      border: light ? '1px solid rgba(0,0,0,0.12)' : '1px solid rgba(255,255,255,0.15)',
      cursor: 'pointer', color: 'var(--ink-2)', fontSize: 15, fontWeight: 700,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>{label}</button>
  );

  const dropdown = open ? (
    <div
      ref={dropdownRef}
      style={{
        position: 'fixed', top: pos.top, left: pos.left, width: pos.width,
        zIndex: 9999,
        background: light ? '#fff' : 'var(--surface)',
        border: light ? '1.5px solid rgba(0,0,0,0.10)' : '1.5px solid rgba(255,255,255,0.12)',
        borderRadius: 14,
        boxShadow: light ? '0 12px 36px rgba(0,0,0,0.16)' : '0 12px 36px rgba(0,0,0,0.5)',
        overflow: 'visible',
      }}
    >
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 14px 10px',
        borderBottom: light ? '1px solid rgba(0,0,0,0.07)' : '1px solid rgba(255,255,255,0.07)',
      }}>
        {navBtn(() => { setYear(y => y - 1); setYearOpen(false); }, '‹')}

        {/* Year button + dropdown */}
        <div style={{ position: 'relative' }}>
          <button
            type="button"
            onClick={() => setYearOpen(y => !y)}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              background: 'none', border: 'none', cursor: 'pointer',
              fontWeight: 700, fontSize: 'var(--fs-sm)', color: 'var(--ink)',
              padding: '4px 8px', borderRadius: 6,
            }}
          >
            {year}
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none"
              style={{ transform: yearOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s', color: 'var(--ink-3)' }}>
              <path d="M2 3.5l3 3 3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

          {yearOpen && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 4px)', left: '50%',
              transform: 'translateX(-50%)',
              background: light ? '#fff' : 'var(--surface)',
              border: light ? '1.5px solid rgba(0,0,0,0.10)' : '1.5px solid rgba(255,255,255,0.12)',
              borderRadius: 10,
              boxShadow: light ? '0 6px 20px rgba(0,0,0,0.13)' : '0 6px 20px rgba(0,0,0,0.45)',
              zIndex: 10, overflow: 'hidden', minWidth: 96,
            }}>
              {years.map((y, idx) => (
                <button
                  key={y}
                  type="button"
                  onClick={() => { setYear(y); setYearOpen(false); }}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '9px 14px', background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: 'var(--fs-sm)', fontWeight: y === year ? 700 : 400,
                    color: y === year ? 'var(--accent)' : 'var(--ink)',
                    borderBottom: idx < years.length - 1
                      ? (light ? '1px solid rgba(0,0,0,0.05)' : '1px solid rgba(255,255,255,0.05)')
                      : 'none',
                  }}
                >
                  <span>{y}</span>
                  {y === year && (
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M2 6l3 3 5-5" stroke="var(--accent)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {navBtn(() => { setYear(y => y + 1); setYearOpen(false); }, '›')}
      </div>

      {/* Month grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4, padding: '10px 12px 12px' }}>
        {MONTH_NAMES.map((m, i) => {
          const isSelected = year === selYear && i === selMonth;
          return (
            <button
              key={m}
              type="button"
              onClick={() => pick(i)}
              style={{
                padding: '9px 4px', borderRadius: 9,
                border: '1.5px solid transparent', cursor: 'pointer',
                fontSize: 'var(--fs-sm)', fontWeight: isSelected ? 700 : 500,
                background: isSelected ? 'var(--accent)' : 'transparent',
                color: isSelected ? '#fff' : 'var(--ink)',
                transition: 'background 0.1s',
              }}
              onMouseEnter={(e) => {
                if (!isSelected) (e.currentTarget as HTMLButtonElement).style.background =
                  light ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.08)';
              }}
              onMouseLeave={(e) => {
                if (!isSelected) (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
              }}
            >
              {m}
            </button>
          );
        })}
      </div>
    </div>
  ) : null;

  return (
    <div style={{ position: 'relative' }}>
      {/* Trigger */}
      <button
        ref={triggerRef}
        type="button"
        onClick={openPicker}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 14px', borderRadius: 10, cursor: 'pointer',
          background: light ? '#fff' : 'var(--surface)',
          border: open
            ? '1.5px solid var(--accent)'
            : light ? '1.5px solid rgba(0,0,0,0.14)' : '1.5px solid rgba(255,255,255,0.16)',
          color: 'var(--ink)', fontSize: 'var(--fs-sm)',
        }}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <rect x="1" y="3" width="14" height="12" rx="2.5" stroke="currentColor" strokeWidth="1.3"/>
          <path d="M1 7h14" stroke="currentColor" strokeWidth="1.3"/>
          <path d="M5 1v3M11 1v3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
        </svg>
        <span style={{ flex: 1, textAlign: 'left', fontWeight: value ? 600 : 400, color: value ? 'var(--ink)' : 'var(--ink-3)' }}>
          {displayLabel}
        </span>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none"
          style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s', color: 'var(--ink-3)' }}>
          <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {/* position:fixed escapes modal overflow clipping */}
      {dropdown}
    </div>
  );
}

// ── Salary Structure Modal ────────────────────────────────────────────────────

function SalaryModal({ employee, existing, templateComponents, onClose }: {
  employee: Employee;
  existing: SalaryStructure | null;
  templateComponents: string[];
  onClose: () => void;
}) {
  const { toast } = useAppStore();
  const queryClient = useQueryClient();

  const defaultComponents = templateComponents.length > 0
    ? templateComponents.map(code => ({ code, amount: 0 }))
    : (existing?.components ?? [{ code: 'basic', amount: 0 }]);

  const [effectiveFrom, setEffectiveFrom] = useState(
    existing?.effectiveFrom?.slice(0, 10) ?? new Date().toISOString().slice(0, 10)
  );
  const [components, setComponents] = useState<Array<{ code: string; amount: number }>>(
    existing
      ? existing.components.map(c => ({ code: c.code, amount: c.amount ?? 0 }))
      : defaultComponents.map(c => ({ code: c.code, amount: (c as { code: string; amount?: number }).amount ?? 0 }))
  );

  const mutation = useMutation({
    mutationFn: () => createSalaryStructureApi({
      employeeId: employee.id,
      effectiveFrom,
      components: components.map(c => ({ code: c.code, amount: c.amount })),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.salaryStructures(employee.id) });
      toast(`Salary structure saved for ${employee.firstName}`, 'success');
      onClose();
    },
    onError: (e: Error) => toast(e.message || 'Failed to save salary structure', 'danger'),
  });

  const empName = `${employee.firstName} ${employee.lastName ?? ''}`.trim();

  return (
    <Modal title={`Salary structure — ${empName}`} onClose={onClose} size="sm">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <label style={{ display: 'block', fontSize: 'var(--fs-sm)', fontWeight: 600, color: 'var(--ink-2)', marginBottom: 6 }}>
            Effective from
          </label>
          <input
            type="date"
            className="t-input"
            value={effectiveFrom}
            onChange={e => setEffectiveFrom(e.target.value)}
          />
        </div>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <label style={{ fontSize: 'var(--fs-sm)', fontWeight: 600, color: 'var(--ink-2)' }}>
              Salary components
            </label>
            <button
              type="button"
              onClick={() => setComponents(cs => [...cs, { code: '', amount: 0 }])}
              style={{ fontSize: 'var(--fs-xs)', color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}
            >
              + Add row
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 28px', gap: 6 }}>
              <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', fontWeight: 600, paddingLeft: 4 }}>CODE</span>
              <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', fontWeight: 600, paddingLeft: 4 }}>AMOUNT (₹/mo)</span>
              <span />
            </div>
            {components.map((c, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 28px', gap: 6, alignItems: 'center' }}>
                <input
                  className="t-input"
                  placeholder="e.g. basic"
                  value={c.code}
                  onChange={e => setComponents(cs => cs.map((x, j) => j === i ? { ...x, code: e.target.value } : x))}
                  style={{ fontFamily: 'monospace', fontSize: 13 }}
                />
                <input
                  type="number"
                  className="t-input"
                  placeholder="50000"
                  value={c.amount || ''}
                  onChange={e => setComponents(cs => cs.map((x, j) => j === i ? { ...x, amount: Number(e.target.value) } : x))}
                  style={{ fontFamily: 'monospace', fontSize: 13 }}
                />
                <button
                  type="button"
                  onClick={() => setComponents(cs => cs.filter((_, j) => j !== i))}
                  style={{ width: 28, height: 28, borderRadius: 6, border: '1px solid var(--border)', background: 'none', cursor: 'pointer', color: 'var(--ink-3)', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >×</button>
              </div>
            ))}
            {components.length === 0 && (
              <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>No components — click "+ Add row" above</p>
            )}
          </div>
          <p style={{ marginTop: 8, fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>
            Component codes must match your payroll template (e.g. <code>basic</code>, <code>hra</code>, <code>pf</code>).
          </p>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 4 }}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            variant="accent"
            disabled={mutation.isPending || components.length === 0 || !effectiveFrom}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? 'Saving…' : 'Save structure'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Salary Structures Section ─────────────────────────────────────────────────

function SalaryStructuresSection({ templateComponents }: { templateComponents: string[] }) {
  const { toast } = useAppStore();
  const { data: employees = [] } = useQuery({ queryKey: qk.employees(), queryFn: fetchEmployees });
  const [selectedEmp, setSelectedEmp] = useState<Employee | null>(null);
  const [empForModal, setEmpForModal] = useState<Employee | null>(null);
  const { theme } = useAppStore();
  const light = theme === 'light';

  const { data: structures = [] } = useQuery({
    queryKey: qk.salaryStructures(selectedEmp?.id ?? ''),
    queryFn: () => fetchSalaryStructures(selectedEmp!.id),
    enabled: !!selectedEmp,
  });

  const activeEmployees = employees.filter(e => e.status === 'active');
  const latest = structures[0] ?? null;

  return (
    <div className="t-card" style={{ marginBottom: 20 }}>
      <div className="card-head">
        <h3>Salary structures</h3>
        <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>
          Assign salary components to employees before running payroll
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: 0 }}>
        {/* Employee list */}
        <div style={{ borderRight: '1px solid var(--border)', maxHeight: 320, overflowY: 'auto' }}>
          {activeEmployees.map(emp => (
            <button
              key={emp.id}
              type="button"
              onClick={() => setSelectedEmp(emp)}
              style={{
                width: '100%', textAlign: 'left', padding: '10px 16px',
                background: selectedEmp?.id === emp.id ? (light ? 'var(--accent-soft, #eef2ff)' : 'rgba(99,102,241,0.12)') : 'none',
                border: 'none', borderBottom: '1px solid var(--border)', cursor: 'pointer',
                color: selectedEmp?.id === emp.id ? 'var(--accent)' : 'var(--ink)',
              }}
            >
              <div style={{ fontSize: 'var(--fs-sm)', fontWeight: selectedEmp?.id === emp.id ? 600 : 400 }}>
                {emp.firstName} {emp.lastName ?? ''}
              </div>
              <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', marginTop: 1 }}>
                {emp.designation?.title ?? emp.jobTitle ?? '—'}
              </div>
            </button>
          ))}
          {activeEmployees.length === 0 && (
            <p style={{ padding: 16, fontSize: 'var(--fs-sm)', color: 'var(--ink-3)' }}>No active employees</p>
          )}
        </div>

        {/* Detail panel */}
        <div style={{ padding: 20 }}>
          {!selectedEmp ? (
            <p style={{ color: 'var(--ink-3)', fontSize: 'var(--fs-sm)' }}>Select an employee to view or set their salary structure.</p>
          ) : latest ? (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                <div>
                  <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 600, color: 'var(--ink)' }}>
                    {selectedEmp.firstName} {selectedEmp.lastName ?? ''}
                  </div>
                  <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', marginTop: 2 }}>
                    Effective from {new Date(latest.effectiveFrom).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => setEmpForModal(selectedEmp)}>
                  Edit structure
                </Button>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>
                    <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 600 }}>COMPONENT</th>
                    <th style={{ textAlign: 'right', padding: '4px 8px', fontWeight: 600 }}>AMOUNT / MO</th>
                  </tr>
                </thead>
                <tbody>
                  {latest.components.map((c, i) => (
                    <tr key={i} style={{ borderTop: '1px solid var(--border)', fontSize: 'var(--fs-sm)' }}>
                      <td style={{ padding: '8px 8px', fontFamily: 'monospace', color: 'var(--ink-2)' }}>{c.code}</td>
                      <td style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 600 }}>
                        {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(c.amount ?? 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <p style={{ color: 'var(--ink-3)', fontSize: 'var(--fs-sm)', marginBottom: 16 }}>
                No salary structure for {selectedEmp.firstName} yet.
              </p>
              <Button variant="accent" onClick={() => setEmpForModal(selectedEmp)}>
                Set salary structure
              </Button>
            </div>
          )}
        </div>
      </div>

      {empForModal && (
        <SalaryModal
          employee={empForModal}
          existing={empForModal.id === selectedEmp?.id ? latest : null}
          templateComponents={templateComponents}
          onClose={() => setEmpForModal(null)}
        />
      )}
    </div>
  );
}

// ── New Run Modal ─────────────────────────────────────────────────────────────

function NewRunModal({ onClose }: { onClose: () => void }) {
  const { toast } = useAppStore();
  const queryClient = useQueryClient();

  const today = new Date();
  const defaultPeriod = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  const [period, setPeriod] = useState(defaultPeriod);
  const [templateId, setTemplateId] = useState('');

  const { data: templates = [], isLoading: loadingTemplates } = useQuery({
    queryKey: qk.payrollTemplates(),
    queryFn: fetchPayrollTemplates,
  });

  React.useEffect(() => {
    if (templates.length && !templateId) setTemplateId(templates[0].id);
  }, [templates]);

  const runMutation = useMutation({
    mutationFn: () => startPayrollRun({ templateId, period }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.payrollRuns() });
      toast('Payroll run created — review and finalize when ready', 'success');
      onClose();
    },
    onError: () => toast('Failed to create payroll run', 'danger'),
  });

  const valid = !!templateId && /^\d{4}-\d{2}$/.test(period);

  return (
    <Modal title="New Payroll Run" onClose={onClose} size="sm">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div>
          <label style={{ display: 'block', fontSize: 'var(--fs-sm)', fontWeight: 600, color: 'var(--ink-2)', marginBottom: 6 }}>
            Template
          </label>
          {loadingTemplates ? (
            <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)' }}>Loading templates…</p>
          ) : templates.length === 0 ? (
            <Alert variant="warn">No payroll templates found. Create one first.</Alert>
          ) : (
            <select
              className="t-input"
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
            >
              {templates.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          )}
        </div>

        <div>
          <label style={{ display: 'block', fontSize: 'var(--fs-sm)', fontWeight: 600, color: 'var(--ink-2)', marginBottom: 8 }}>
            Period
          </label>
          <MonthPicker value={period} onChange={setPeriod} />
          <p style={{ marginTop: 8, fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>
            Payroll will be computed for all employees with an active salary structure.
          </p>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 4 }}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            variant="accent"
            onClick={() => runMutation.mutate()}
            disabled={!valid || runMutation.isPending}
          >
            {runMutation.isPending ? 'Running…' : 'Run Payroll'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Main screen ───────────────────────────────────────────────────────────────

export default function Payslips() {
  const { role, employeeId, profileId, toast } = useAppStore();
  const myId = employeeId || profileId;
  const queryClient = useQueryClient();
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);
  const [confirmRunId, setConfirmRunId] = useState<string | null>(null);
  const [showNewRun, setShowNewRun] = useState(false);
  const [selectedSlip, setSelectedSlip] = useState<Payslip | null>(null);
  const [filterPeriod, setFilterPeriod] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const { data: payrollRuns = [] } = useQuery({
    queryKey: qk.payrollRuns(),
    queryFn: fetchPayrollRuns,
    enabled: role === 'hr',
  });

  const finalizeMutation = useMutation({
    mutationFn: (runId: string) => finalizePayrollRunApi(runId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.payrollRuns() });
      setConfirmRunId(null);
      toast('Payroll run finalized — payslips are now locked', 'success');
    },
    onError: () => toast('Failed to finalize run', 'danger'),
  });

  const markPaidMutation = useMutation({
    mutationFn: (runId: string) => markPayrollRunPaid(runId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.payrollRuns() });
      toast('Run marked as paid — salaries disbursed', 'success');
    },
    onError: () => toast('Failed to mark as paid', 'danger'),
  });

  const bankFileMutation = useMutation({
    mutationFn: (run: PayrollRun) => fetchBankFileCsv(run.id),
    onSuccess: (csv, run) => {
      downloadCsv(csv, `bank-file-${run.label.replace(/\s+/g, '-')}.csv`);
      toast('Bank file downloaded', 'success');
    },
    onError: () => toast('Failed to download bank file', 'danger'),
  });

  // ESS/MSS: dedicated endpoint — no HR role required
  const { data: mySlips = [] } = useQuery({
    queryKey: qk.myPayslips(),
    queryFn: fetchMyPayslips,
    enabled: role !== 'hr',
  });

  const { theme } = useAppStore();
  const light = theme === 'light';

  const { data: payrollTemplates = [] } = useQuery({
    queryKey: qk.payrollTemplates(),
    queryFn: fetchPayrollTemplates,
    enabled: role === 'hr',
  });

  // ── HR view ────────────────────────────────────────────────────────────────
  if (role === 'hr') {
    const topRun = payrollRuns[0];
    // Extract component codes from the first template so SalaryModal can pre-fill them
    const firstTemplate = payrollTemplates[0];
    const templateCodes: string[] = (() => {
      if (!firstTemplate) return [];
      const raw = firstTemplate.components as unknown;
      // components field may be the full config object { components: [...] } or already an array
      const arr = Array.isArray(raw)
        ? raw
        : Array.isArray((raw as { components?: unknown[] })?.components)
          ? (raw as { components: Array<{ code: string }> }).components
          : [];
      return (arr as Array<{ code: string }>).map(c => c.code);
    })();

    return (
      <div>
        <div className="sk-page-header">
          <h1>Payroll</h1>
          <Button variant="accent" onClick={() => setShowNewRun(true)}>
            <Icon name="plus" size={14} color="white" />
            New Run
          </Button>
        </div>

        {/* Stage indicator for most recent run */}
        {topRun && (
          <div className="t-card" style={{ marginBottom: 20, padding: '20px 24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 'var(--fs-base)', fontWeight: 700 }}>
                  {topRun.label}
                </h3>
                <p style={{ margin: '2px 0 0', fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>
                  {topRun.headcount} employees · {fmt(topRun.totalNetMinor)} net
                </p>
              </div>
              <Chip variant={topRun.status === 'paid' ? 'success' : topRun.status === 'finalized' ? 'info' : 'neutral'}>
                {topRun.status}
              </Chip>
            </div>
            <StageBar status={topRun.status} />
          </div>
        )}

        {/* Salary structures */}
        <SalaryStructuresSection templateComponents={templateCodes} />

        {/* Runs table */}
        <div className="t-card">
          <div className="card-head"><h3>Payroll Runs</h3></div>
          <table className="t-table">
            <thead>
              <tr>
                <th style={{ width: 28 }}></th>
                <th>Period</th>
                <th>Headcount</th>
                <th>Total Gross</th>
                <th>Total Net</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {payrollRuns.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', color: 'var(--ink-3)', padding: 40 }}>
                    No payroll runs yet. Click "New Run" to get started.
                  </td>
                </tr>
              )}
              {payrollRuns.map((run) => {
                const expanded = expandedRunId === run.id;
                return (
                  <React.Fragment key={run.id}>
                    <tr
                      style={{ cursor: 'pointer', background: expanded ? (light ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.03)') : undefined }}
                    >
                      <td
                        onClick={() => setExpandedRunId(expanded ? null : run.id)}
                        style={{ textAlign: 'center', color: 'var(--ink-3)', userSelect: 'none' }}
                      >
                        <span style={{
                          display: 'inline-block',
                          transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                          transition: 'transform 0.15s',
                          fontSize: 10,
                        }}>▶</span>
                      </td>
                      <td onClick={() => setExpandedRunId(expanded ? null : run.id)} style={{ fontWeight: 500 }}>
                        {run.label}
                      </td>
                      <td onClick={() => setExpandedRunId(expanded ? null : run.id)}>{run.headcount}</td>
                      <td onClick={() => setExpandedRunId(expanded ? null : run.id)} className="mono">
                        {fmt(run.totalGrossMinor)}
                      </td>
                      <td onClick={() => setExpandedRunId(expanded ? null : run.id)} className="mono" style={{ fontWeight: 600 }}>
                        {fmt(run.totalNetMinor)}
                      </td>
                      <td onClick={() => setExpandedRunId(expanded ? null : run.id)}>
                        <Chip variant={run.status === 'paid' ? 'success' : run.status === 'finalized' ? 'info' : 'neutral'}>
                          {run.status}
                        </Chip>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'nowrap' }}>
                          {run.status === 'draft' && (
                            <Button variant="accent" size="sm" onClick={() => setConfirmRunId(run.id)}>
                              Finalize
                            </Button>
                          )}
                          {run.status === 'finalized' && (
                            <>
                              <Button
                                variant="accent"
                                size="sm"
                                onClick={() => markPaidMutation.mutate(run.id)}
                                disabled={markPaidMutation.isPending}
                              >
                                <Icon name="check" size={12} color="white" />
                                Mark Paid
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => bankFileMutation.mutate(run)}
                                disabled={bankFileMutation.isPending}
                              >
                                <Icon name="download" size={12} color="var(--ink-2)" />
                                Bank File
                              </Button>
                            </>
                          )}
                          {run.status === 'paid' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => bankFileMutation.mutate(run)}
                              disabled={bankFileMutation.isPending}
                            >
                              <Icon name="download" size={12} color="var(--ink-2)" />
                              Bank File
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expanded && <RunPayslips runId={run.id} />}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* New Run modal */}
        {showNewRun && <NewRunModal onClose={() => setShowNewRun(false)} />}

        {/* Finalize confirm */}
        {confirmRunId && (() => {
          const run = payrollRuns.find((r) => r.id === confirmRunId);
          return run ? (
            <FinalizeConfirm
              runLabel={run.label}
              onConfirm={() => finalizeMutation.mutate(confirmRunId)}
              onCancel={() => setConfirmRunId(null)}
            />
          ) : null;
        })()}
      </div>
    );
  }

  // ── ESS / MSS view ─────────────────────────────────────────────────────────
  return (
    <div>
      <div className="sk-page-header">
        <h1>Payslips</h1>
      </div>

      <div className="t-card">
        <div className="card-head" style={{ flexWrap: 'wrap', gap: 8 }}>
          <h3>My payslips</h3>
          <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
            <select
              className="t-input"
              style={{ width: 'auto', padding: '4px 8px', fontSize: 'var(--fs-sm)' }}
              value={filterPeriod}
              onChange={(e) => setFilterPeriod(e.target.value)}
            >
              <option value="">All periods</option>
              {Array.from(new Set(mySlips.map((p) => p.periodLabel))).map((l) => (
                <option key={l} value={l}>{l}</option>
              ))}
            </select>
            <select
              className="t-input"
              style={{ width: 'auto', padding: '4px 8px', fontSize: 'var(--fs-sm)' }}
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">All status</option>
              <option value="paid">Paid</option>
              <option value="draft">Draft</option>
            </select>
          </div>
        </div>
        <table className="t-table">
          <thead>
            <tr>
              <th>Period</th><th>Gross pay</th><th>Deductions</th><th>Net pay</th><th>Status</th><th></th>
            </tr>
          </thead>
          <tbody>
            {mySlips.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', color: 'var(--ink-3)', padding: 32 }}>
                  No payslips yet
                </td>
              </tr>
            ) : mySlips
              .filter((p) =>
                (!filterPeriod || p.periodLabel === filterPeriod) &&
                (!filterStatus || (filterStatus === 'paid' ? !!p.paidAt : !p.paidAt))
              )
              .map((p) => (
                <tr key={p.id}>
                  <td style={{ fontWeight: 500 }}>{p.periodLabel}</td>
                  <td className="mono">{fmt(p.grossPayMinor)}</td>
                  <td className="mono" style={{ color: 'var(--danger)' }}>-{fmt(p.totalDeductionsMinor)}</td>
                  <td className="mono" style={{ fontWeight: 600 }}>{fmt(p.netPayMinor)}</td>
                  <td><Chip variant={p.paidAt ? 'success' : 'warn'}>{p.paidAt ? 'Paid' : 'Draft'}</Chip></td>
                  <td>
                    <Button variant="ghost" size="sm" onClick={() => setSelectedSlip(p)}>
                      <Icon name="eye" size={13} color="var(--ink-2)" />
                      View
                    </Button>
                  </td>
                </tr>
              ))
            }
          </tbody>
        </table>
      </div>

      {/* Payslip detail modal */}
      {selectedSlip && (
        <Modal
          title={`Payslip · ${selectedSlip.periodLabel}`}
          onClose={() => setSelectedSlip(null)}
          size="wide"
          footer={
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button variant="ghost" onClick={() => toast('Knack AI explanation coming in V2', 'info')}>
                <Icon name="star" size={13} color="var(--accent)" />
                Explain with Knack AI
              </Button>
              {/*
                * CH-7.3 / CH-U2 — the real document now exists. Opened in a new
                * tab rather than fetched and blob-downloaded: the endpoint
                * answers with Content-Disposition: inline, so the browser
                * previews it and the user's own Save works, and the request
                * carries the session cookie automatically.
                */}
              <Button
                variant="outline"
                onClick={() => {
                  if (!selectedSlip?.id) { toast('Payslip not available', 'warn'); return; }
                  window.open(payslipPdfUrl(selectedSlip.id), '_blank', 'noopener');
                }}
              >
                <Icon name="download" size={13} color="var(--ink-2)" />
                Download PDF
              </Button>
            </div>
          }
        >
          <div style={{ marginBottom: 16 }}>
            <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)' }}>
              {selectedSlip.periodStart} – {selectedSlip.periodEnd}
            </p>
          </div>
          {selectedSlip.lines && selectedSlip.lines.length > 0 ? (
            <table className="t-table">
              <thead><tr><th>Component</th><th style={{ textAlign: 'right' }}>Amount</th></tr></thead>
              <tbody>
                {selectedSlip.lines.map((l) => (
                  <tr key={l.id}>
                    <td>{l.label}</td>
                    <td className="mono" style={{ textAlign: 'right', color: l.isDeduction ? 'var(--danger)' : 'var(--ink)' }}>
                      {l.isDeduction ? '-' : ''}{fmt(l.amountMinor)}
                    </td>
                  </tr>
                ))}
                <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                  <td>Net Pay</td>
                  <td className="mono" style={{ textAlign: 'right', color: 'var(--success)' }}>
                    {fmt(selectedSlip.netPayMinor)}
                  </td>
                </tr>
              </tbody>
            </table>
          ) : (
            <Alert variant="info">Detailed breakdown not available for this payslip.</Alert>
          )}
        </Modal>
      )}
    </div>
  );
}
